import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Eye, 
  Edit3,
  Save,
  X,
  Download,
  Settings,
  Brain,
  Shield,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import type { UserDocument, DocumentType } from '../types/database';
import { ProfitLossMappingInterface } from './ProfitLossMappingInterface';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ExtractionData {
  id: string;
  filename: string;
  document_type: DocumentType;
  extraction_status: string;
  validation_status: string;
  confidence_scores?: Record<string, number>;
  validation_errors: string[];
  extractor_results: any;
  challenger_results: any;
  consensus_data: any;
  corrected_data: any;
  created_at: string;
  updated_at: string;
}

interface DocumentExtractionViewProps {
  selectedDocument: UserDocument;
  onClose: () => void;
  onExtractionComplete?: (extractionId: string) => void;
}

export function DocumentExtractionView({ 
  selectedDocument, 
  onClose,
  onExtractionComplete 
}: DocumentExtractionViewProps) {
  const { user } = useUser();
  const [extractionData, setExtractionData] = useState<ExtractionData | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Helper function to get the best data to display based on document type
  const getDisplayData = (data: ExtractionData) => {
    if (!data) return null;

    // If user has corrected data, always use that
    if (data.corrected_data) {
      return data.corrected_data;
    }

    // For inventory lists and similar documents, prefer extractor_results which contain the actual items
    if (selectedDocument?.document_type === 'inventory_list' || 
        selectedDocument?.document_type === 'asset_list') {
      if (data.extractor_results && Object.keys(data.extractor_results).length > 0) {
        return data.extractor_results;
      }
    }

    // For financial documents, prefer consensus_data if it has meaningful values
    if (data.consensus_data) {
      // Check if consensus_data has the nested structure
      const consensusContent = data.consensus_data.consensus_data || data.consensus_data;
      
      // Check if any financial fields have non-null, non-zero values
      const hasFinancialData = Object.values(consensusContent).some(value => 
        value !== null && value !== 0 && value !== '0' && value !== ''
      );
      
      if (hasFinancialData) {
        return consensusContent;
      }
    }

    // Fall back to extractor_results
    return data.extractor_results || {};
  };
  const [editedData, setEditedData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showMappingInterface, setShowMappingInterface] = useState(false);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  
  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [autoFit, setAutoFit] = useState<boolean>(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showRawText, setShowRawText] = useState<boolean>(false);

  // Check if extraction already exists for this document
  useEffect(() => {
    if (selectedDocument?.metadata?.extraction_id) {
      loadExistingExtraction(selectedDocument.metadata.extraction_id);
    } else if (selectedDocument?.id) {
      loadExistingExtractionByDocumentId(selectedDocument.id);
    }
  }, [selectedDocument]);

  // Load document preview
  useEffect(() => {
    if (selectedDocument && user) {
      loadDocumentPreview();
    } else if (selectedDocument && !user) {
      setError('User not authenticated');
    }
  }, [selectedDocument, user]);

  // Handle window resize for auto-fit
  useEffect(() => {
    const handleResize = () => {
      if (autoFit) {
        calculateAutoFitScale();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [autoFit]);


  // Trigger auto-fit when document loads and auto-fit is enabled
  useEffect(() => {
    if (previewUrl && autoFit && numPages > 0) {
      // Small delay to ensure PDF is fully rendered
      const timeoutId = setTimeout(() => {
        calculateAutoFitScale();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [previewUrl, autoFit, numPages]);

  const loadDocumentPreview = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await DatabaseService.getDocumentForExtraction(selectedDocument.id, user?.id);
      
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setPreviewUrl(response.data.url);
      } else {
        setError('No preview data received');
      }
    } catch (err) {
      setError(`Failed to load document preview: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingExtraction = async (extractionId: string) => {
    try {
      setIsLoading(true);
      const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
      const url = `${baseUrl}/extraction-results/${extractionId}`;
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setExtractionData(data);
        setEditedData(data.corrected_data || data.consensus_data || data.extractor_results);
      } else {
        const errorText = await response.text();
        
        // If direct extraction ID fails, try fallback to document ID method
        if (response.status === 500 && selectedDocument?.id) {
          await loadExistingExtractionByDocumentId(selectedDocument.id);
          return;
        }
        
        setError(`Failed to load extraction results: ${response.status} ${errorText}`);
      }
    } catch (err) {
      setError(`Failed to load extraction: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingExtractionByDocumentId = async (documentId: string) => {
    try {
      setIsLoading(true);
      const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
      const url = `${baseUrl}/extraction-results/by-document/${documentId}`;
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setExtractionData(data);
        setEditedData(data.corrected_data || data.consensus_data || data.extractor_results);
      } else if (response.status === 404) {
        // This is expected for documents that haven't been extracted yet
        setExtractionData(null);
        setEditedData(null);
      } else {
        const errorText = await response.text();
        setError(`Failed to load extraction results: ${response.status} ${errorText}`);
      }
    } catch (err) {
      setError(`Failed to load extraction: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const startExtraction = async () => {
    if (!selectedDocument) return;

    setIsLoading(true);
    setError(null);
    setMessage(null);
    
    try {
      const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
      
      // First, get the document file
      const downloadResponse = await DatabaseService.getDocumentForExtraction(selectedDocument.id, user?.id);
      if (downloadResponse.error) {
        throw new Error(downloadResponse.error);
      }
      
      // Fetch the file from the signed URL
      const fileResponse = await fetch(downloadResponse.data!.url);
      if (!fileResponse.ok) {
        throw new Error(`Failed to download document: HTTP ${fileResponse.status}`);
      }
      
      const fileBlob = await fileResponse.blob();
      
      // Validate file size
      if (fileBlob.size === 0) {
        throw new Error('Downloaded file is empty. Please check the document and try again.');
      }
      
      // Validate file type
      if (fileBlob.type !== 'application/pdf' && !selectedDocument.file_name.toLowerCase().endsWith('.pdf')) {
        throw new Error('Only PDF files are supported for extraction.');
      }
      
      const file = new File([fileBlob], selectedDocument.file_name, { type: 'application/pdf' });
      
      // Upload for extraction
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', selectedDocument.document_type);
      if (user?.id) formData.append('user_id', user.id);
      
      setMessage('Uploading document for processing...');
      
      const uploadResponse = await fetch(`${baseUrl}/upload-document`, {
        method: 'POST',
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Upload failed: ${uploadResponse.status} - ${errorText}`);
      }
      
      const result = await uploadResponse.json();
      
      if (result.extraction_id) {
        setMessage('Document uploaded successfully. Starting extraction...');
        // Poll for extraction status
        pollExtractionStatus(result.extraction_id);
      } else {
        throw new Error(result.detail || 'Failed to start extraction - no extraction ID returned');
      }
    } catch (err) {
      console.error('Extraction failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start extraction';
      setError(errorMessage);
      setMessage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const pollExtractionStatus = async (extractionId: string) => {
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;

    const poll = async () => {
      try {
        const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
        
        const response = await fetch(`${baseUrl}/extraction-status/${extractionId}`);
        
        if (!response.ok) {
          throw new Error(`Status check failed: HTTP ${response.status}`);
        }
        
        const status = await response.json();

        if (status.extraction_status === 'completed') {
          setMessage('Extraction completed! Loading results...');
          
          // Get full results
          const resultsResponse = await fetch(`${baseUrl}/extraction-results/${extractionId}`);
          if (!resultsResponse.ok) {
            throw new Error(`Failed to fetch results: HTTP ${resultsResponse.status}`);
          }
          
          const results = await resultsResponse.json();
          setExtractionData(results);
          setEditedData(results.corrected_data || results.consensus_data || results.extractor_results);
          setMessage('Extraction completed successfully!');
          onExtractionComplete?.(extractionId);
        } else if (status.extraction_status === 'error') {
          console.error('Extraction failed:', status.status_message);
          let errorMessage = status.status_message || 'Unknown error occurred';
          
          // Provide more helpful error messages
          if (errorMessage.includes('No text could be extracted')) {
            errorMessage = 'Unable to extract text from this PDF. This may be a scanned document. The system will attempt OCR processing, but results may vary.';
          } else if (errorMessage.includes('Document type mismatch')) {
            errorMessage = `Document type mismatch: ${errorMessage}. The system detected a different document type than expected.`;
          }
          
          setError(`Extraction failed: ${errorMessage}`);
          setMessage(null);
        } else if (status.extraction_status === 'processing') {
          // Continue polling with progress updates
          attempts++;
          if (attempts < maxAttempts) {
            const progressMessage = status.status_message || 'Processing document...';
            const timeElapsed = Math.floor((attempts * 5) / 60) > 0 ? `${Math.floor((attempts * 5) / 60)}m ${(attempts * 5) % 60}s` : `${attempts * 5}s`;
            setMessage(`${progressMessage} - ${timeElapsed} elapsed`);
            setTimeout(poll, 5000); // Poll every 5 seconds
          } else {
            setError('Extraction timed out after 5 minutes. The document may be too complex or there may be a server issue. Please try again.');
            setMessage(null);
          }
        } else if (attempts < maxAttempts) {
          // Handle other statuses
          attempts++;
          setMessage(`Processing... (${attempts}/${maxAttempts})`);
          setTimeout(poll, 5000);
        } else {
          setError('Extraction timed out. Please try again.');
          setMessage(null);
        }
      } catch (error) {
        console.error('Error polling status:', error);
        const errorMessage = error instanceof Error ? error.message : 'Error checking extraction status';
        setError(errorMessage);
        setMessage(null);
      }
    };

    poll();
  };

  const handleSaveCorrections = async () => {
    if (!extractionData || !editedData) return;

    try {
      setIsLoading(true);
      const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
      const response = await fetch(`${baseUrl}/save-extraction-edits/${extractionData.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editedData),
      });

      if (response.ok) {
        // Update the local state with the saved data
        setExtractionData({ 
          ...extractionData, 
          corrected_data: editedData,
          validation_status: 'user_corrected'
        });
        setIsEditing(false);
        setMessage('Edits saved successfully');
        setTimeout(() => setMessage(null), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save edits');
      }
    } catch (error) {
      console.error('Error saving edits:', error);
      setError(`Failed to save edits: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveMappedData = async (mappedData: any) => {
    setIsSavingMapping(true);
    setError(null);
    
    try {
      const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
      
      const response = await fetch(`${baseUrl}/save-extraction-edits/${extractionData?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(mappedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save mapped data');
      }

      // Update the local state with the saved data
      setExtractionData({ 
        ...extractionData, 
        corrected_data: mappedData,
        validation_status: 'validated'
      });
      setShowMappingInterface(false);
      setMessage('Mapped data saved successfully');
      setTimeout(() => setMessage(null), 3000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mapped data');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSavingMapping(false);
    }
  };

  const handleCancelMapping = () => {
    setShowMappingInterface(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    if (autoFit) {
      // Delay the auto-fit calculation to ensure the PDF is fully rendered
      setTimeout(() => {
        calculateAutoFitScale();
      }, 500);
    }
  };

  const onDocumentLoadError = (error: any) => {
    setError(`Failed to load PDF: ${error.message || 'Unknown PDF error'}`);
  };

  const calculateAutoFitScale = () => {
    try {
      // Use a timeout to ensure DOM is ready
      setTimeout(() => {
        try {
          const container = document.querySelector('.pdf-preview-container');
          if (!container) {
            return;
          }
          
          // Get the actual available space in the container, accounting for padding and scrollbars
          // Container has p-4 (16px each side) + inner p-6 (24px each side) = 80px total width padding
          const containerWidth = container.clientWidth - 80; // Account for all padding layers
          const containerHeight = container.clientHeight - 80; // Account for all padding layers and navigation
          
          // Ensure we have valid dimensions
          if (containerWidth <= 0 || containerHeight <= 0) {
            return;
          }
          
          // Calculate scale based on the 800px base width used in the Page component
          // We want the scaled width (800 * scale) to fit within the container width
          const baseWidth = 800;
          const maxScaleByWidth = containerWidth / baseWidth;
          
          // Also consider height - A4 ratio is about 1.414 (height/width)
          const baseHeight = baseWidth * 1.414; // Approximate A4 height
          const maxScaleByHeight = containerHeight / baseHeight;
          
          // Use the smaller scale to ensure it fits both width and height
          const newScale = Math.min(maxScaleByWidth, maxScaleByHeight);
          
          // Set a reasonable minimum and maximum scale for auto-fit (conservative for better UX)
          const finalScale = Math.max(0.3, Math.min(1.2, newScale));
          
          setScale(finalScale);
        } catch (error) {
          // Silently handle auto-fit calculation errors
        }
      }, 300); // Slightly longer timeout to ensure PDF is rendered
    } catch (error) {
      // Silently handle auto-fit setup errors
    }
  };

  const changePage = (offset: number) => {
    setPageNumber(prevPageNumber => {
      const newPageNumber = prevPageNumber + offset;
      return Math.max(1, Math.min(numPages, newPageNumber));
    });
  };

  const changeScale = (delta: number) => {
    setAutoFit(false); // Disable auto-fit when manually adjusting scale
    setScale(prevScale => Math.max(0.2, Math.min(5.0, prevScale + delta)));
  };

  const toggleAutoFit = () => {
    if (autoFit) {
      // Currently in auto-fit mode, disable it
      setAutoFit(false);
    } else {
      // Currently in manual mode, enable auto-fit and calculate scale
      setAutoFit(true);
      calculateAutoFitScale();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-[95vw] h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Document Extraction</h2>
              <p className="text-sm text-gray-600">{selectedDocument.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        {message && (
          <div className="mx-4 mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <p className="text-green-800 text-sm">{message}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mx-4 mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
              <button
                onClick={startExtraction}
                disabled={isLoading}
                className="ml-4 px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Document Preview */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Document Preview</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => changeScale(-0.1)}
                    className="p-1 text-gray-600 hover:text-gray-800"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">{Math.round(scale * 100)}%</span>
                  <button
                    onClick={() => changeScale(0.1)}
                    className="p-1 text-gray-600 hover:text-gray-800"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={toggleAutoFit}
                    className={`px-3 py-1 text-xs font-medium rounded ${
                      autoFit 
                        ? 'bg-blue-600 text-white border border-blue-600' 
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                    title={autoFit ? "Auto-fit enabled - click to disable" : "Click to auto-fit document to container"}
                  >
                    {autoFit ? 'Fit ✓' : 'Fit'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-hidden pdf-preview-container p-4">
              {isLoading && !previewUrl ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : previewUrl ? (
                <div className="h-full flex flex-col">
                  <div className="flex-1 overflow-auto bg-gray-50 rounded-lg">
                    <div className="p-6 min-h-full" style={{ minWidth: `${800 * scale + 48}px` }}>
                      <div className="flex justify-center">
                        <Document
                          file={previewUrl}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onLoadError={onDocumentLoadError}
                          loading={
                            <div className="flex items-center justify-center h-96">
                              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                              <span className="ml-2 text-gray-600">Loading PDF...</span>
                            </div>
                          }
                        >
                          <Page
                            pageNumber={pageNumber}
                            width={800 * scale}
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                            onLoadError={(error) => {
                              setError(`Failed to load PDF page: ${error.message || 'Unknown page error'}`);
                            }}
                          />
                        </Document>
                      </div>
                    </div>
                  </div>
                  
                  {numPages > 1 && (
                    <div className="flex items-center justify-center gap-4 p-3 bg-gray-50 border-t border-gray-200">
                      <button
                        onClick={() => changePage(-1)}
                        disabled={pageNumber <= 1}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-white hover:bg-gray-100 rounded-lg disabled:opacity-50 border border-gray-300"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </button>
                      <span className="text-sm text-gray-700 font-medium">
                        Page {pageNumber} of {numPages}
                      </span>
                      <button
                        onClick={() => changePage(1)}
                        disabled={pageNumber >= numPages}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-white hover:bg-gray-100 rounded-lg disabled:opacity-50 border border-gray-300"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Failed to load document preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Extraction Data */}
          <div className="w-1/2 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Extracted Data</h3>
                {extractionData && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                    {isEditing && (
                      <button
                        onClick={handleSaveCorrections}
                        disabled={isLoading}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        Save
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1 p-4 overflow-y-auto">
              {!extractionData ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <Brain className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No extraction data available</p>
                  <p className="text-sm text-center mb-6">
                    {selectedDocument?.metadata?.extraction_id 
                      ? 'Loading existing extraction data...' 
                      : 'Click the button below to start extracting financial data from this document'
                    }
                  </p>
                  {!selectedDocument?.metadata?.extraction_id && (
                    <button
                      onClick={startExtraction}
                      disabled={isLoading || !previewUrl}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : !previewUrl ? (
                        <>
                          <AlertCircle className="w-5 h-5" />
                          Document Preview Required
                        </>
                      ) : (
                        <>
                          <Brain className="w-5 h-5" />
                          Start Extraction
                        </>
                      )}
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Status Header */}
                  <div className="flex items-center gap-3">
                    {getStatusIcon(extractionData.extraction_status)}
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Extraction Status</h4>
                      <p className="text-sm text-gray-600">
                        {extractionData.extraction_status.replace('_', ' ').toUpperCase()}
                      </p>
                      {extractionData.status_message && (
                        <p className="text-sm text-gray-500 mt-1">
                          {extractionData.status_message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Error-specific help */}
                  {extractionData.extraction_status === 'error' && extractionData.status_message?.includes('No text could be extracted') && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <h5 className="font-medium text-yellow-800 mb-2">Possible Solutions</h5>
                          <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                            <li>This might be a <strong>scanned document</strong> or image-based PDF</li>
                            <li>Try uploading a PDF with selectable text instead of a scanned image</li>
                            <li>If this is a scanned document, ensure OCR (Optical Character Recognition) is enabled on the server</li>
                            <li>Check if the PDF file is corrupted or has unusual formatting</li>
                          </ul>
                          <button
                            onClick={startExtraction}
                            disabled={isLoading}
                            className="mt-3 px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors disabled:opacity-50"
                          >
                            Retry Extraction
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* General error help */}
                  {extractionData.extraction_status === 'error' && !extractionData.status_message?.includes('No text could be extracted') && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <h5 className="font-medium text-red-800 mb-2">Extraction Failed</h5>
                          <p className="text-sm text-red-700 mb-3">
                            {extractionData.status_message || 'An unknown error occurred during extraction'}
                          </p>
                          <button
                            onClick={startExtraction}
                            disabled={isLoading}
                            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                          >
                            Retry Extraction
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Raw Extracted Text */}
                  {extractionData.raw_text && (
                    <div>
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded-lg transition-colors"
                        onClick={() => setShowRawText(!showRawText)}
                      >
                        <h4 className="font-medium text-gray-900">Raw Extracted Text</h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {extractionData.raw_text.length.toLocaleString()} chars
                          </span>
                          {showRawText ? (
                            <ChevronUp className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          )}
                        </div>
                      </div>
                      
                      {showRawText && (
                        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="p-3 border-b border-gray-200 bg-gray-100 rounded-t-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-600" />
                                <span className="text-sm font-medium text-gray-700">
                                  OCR/PDF Extraction Results
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {extractionData.raw_text.split(/\s+/).length.toLocaleString()} words
                              </div>
                            </div>
                          </div>
                          <div className="p-4 max-h-64 overflow-y-auto">
                            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
                              {extractionData.raw_text}
                            </pre>
                          </div>
                          <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                            <p className="text-xs text-gray-600">
                              💡 This is the raw text extracted from your PDF before AI processing. Use this to verify that OCR/text extraction is working correctly.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                  {/* Validation Errors */}
                  {extractionData.validation_errors && extractionData.validation_errors.length > 0 && (
                    <div>
                      <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Validation Errors
                      </h4>
                      <div className="space-y-2">
                        {extractionData.validation_errors.map((error, index) => (
                          <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-800">{error}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Extracted Data */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Extracted Data</h4>
                      {selectedDocument?.document_type === 'profit_loss_statement' && !showMappingInterface && (
                        <button
                          onClick={() => setShowMappingInterface(true)}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          Map Data
                        </button>
                      )}
                    </div>
                    
                    {showMappingInterface && selectedDocument?.document_type === 'profit_loss_statement' ? (
                      <ProfitLossMappingInterface
                        extractedData={getDisplayData(extractionData)}
                        onSave={handleSaveMappedData}
                        onCancel={handleCancelMapping}
                        isLoading={isSavingMapping}
                        dealId={selectedDocument.deal_id}
                        extractionId={extractionData?.id}
                      />
                    ) : (
                      <ExtractedDataDisplay
                        data={isEditing ? editedData : getDisplayData(extractionData)}
                        isEditing={isEditing}
                        onDataChange={setEditedData}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Extracted Data Display Component
function ExtractedDataDisplay({ 
  data, 
  isEditing, 
  onDataChange 
}: { 
  data: any; 
  isEditing: boolean; 
  onDataChange: (data: any) => void;
}) {
  if (!data) {
    return (
      <div className="text-center text-gray-500 py-8">
        <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>No data extracted</p>
      </div>
    );
  }

  const handleValueChange = (field: string, value: string) => {
    if (isEditing && onDataChange) {
      const newData = { ...data, [field]: value };
      onDataChange(newData);
    }
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) {
      return '';
    }
    
    if (Array.isArray(value)) {
      return (
        <div className="space-y-1">
          {value.map((item, index) => (
            <div key={index} className="px-2 py-1 bg-white rounded border text-sm">
              {item?.toString() || ''}
            </div>
          ))}
        </div>
      );
    }
    
    if (typeof value === 'number') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    return value?.toString() || '';
  };

  const formatFieldName = (field: string) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/\b(And|Or|Of|In|At|To|For|With|By)\b/g, l => l.toLowerCase());
  };

  const isFinancialField = (field: string, value: any) => {
    // If the value is an array, it's likely a list of items, not a financial amount
    if (Array.isArray(value)) {
      return false;
    }
    
    const financialFields = [
      'revenue', 'income', 'expense', 'profit', 'loss', 'cost', 'price', 'amount', 
      'total', 'net', 'gross', 'balance', 'liabilities', 'equity',
      'cash', 'debt', 'loan', 'payment', 'fee', 'tax', 'depreciation'
    ];
    return financialFields.some(f => field.toLowerCase().includes(f));
  };

  const groupFields = (data: any) => {
    const groups: { [key: string]: Array<[string, any]> } = {
      'Revenue & Income': [],
      'Expenses & Costs': [],
      'Financial Metrics': [],
      'Other Information': []
    };

    Object.entries(data).forEach(([field, value]) => {
      const fieldLower = field.toLowerCase();
      
      if (fieldLower.includes('revenue') || fieldLower.includes('income') || fieldLower.includes('sales')) {
        groups['Revenue & Income'].push([field, value]);
      } else if (fieldLower.includes('expense') || fieldLower.includes('cost') || fieldLower.includes('operating')) {
        groups['Expenses & Costs'].push([field, value]);
      } else if (isFinancialField(field, value)) {
        groups['Financial Metrics'].push([field, value]);
      } else {
        groups['Other Information'].push([field, value]);
      }
    });

    // Remove empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, fields]) => fields.length > 0)
    );
  };

  const groupedData = groupFields(data);

  return (
    <div className="space-y-6">
      {Object.entries(groupedData).map(([groupName, fields]) => (
        <div key={groupName} className="space-y-3">
          <h5 className="font-semibold text-gray-800 border-b border-gray-200 pb-2">
            {groupName}
          </h5>
          <div className="space-y-2">
            {fields.map(([field, value]) => (
              <div key={field} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <span className="font-medium text-gray-700">
                  {formatFieldName(field)}
                </span>
                {isEditing ? (
                  <input
                    type={isFinancialField(field, value) ? "number" : "text"}
                    value={formatValue(value)}
                    onChange={(e) => handleValueChange(field, e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px] text-right"
                    placeholder="Enter value..."
                  />
                ) : (
                  <span className={`text-gray-900 font-medium ${isFinancialField(field, value) ? 'text-right' : ''}`}>
                    {formatValue(value)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
